import { useEffect, useRef, useState } from 'react';

const MIN_HEIGHT = 150;
const MAX_HEIGHT = 500;

const measureContentHeight = (textarea: HTMLTextAreaElement) => {
  const prevHeight = textarea.style.getPropertyValue('height');
  const prevPriority = textarea.style.getPropertyPriority('height');

  textarea.style.setProperty('height', '0px', 'important');
  const contentHeight = textarea.scrollHeight;

  if (prevHeight) {
    textarea.style.setProperty('height', prevHeight, prevPriority);
  } else {
    textarea.style.removeProperty('height');
  }

  return contentHeight;
};

export const useResizableTextarea = (value: string) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const updateScroll = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const contentHeight = measureContentHeight(textarea);
      const needsScroll = contentHeight > MIN_HEIGHT;

      if (document.activeElement === textarea) {
        selectionRef.current = {
          start: textarea.selectionStart,
          end: textarea.selectionEnd,
        };
      }
      setScrollHeight(contentHeight);
      setHasScroll(needsScroll);
    };

    const frameId = requestAnimationFrame(updateScroll);
    window.addEventListener('resize', updateScroll);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateScroll);
    };
  }, [value]);

  useEffect(() => {
    const wrap = resizeRef.current;
    const textarea = textareaRef.current;
    if (!wrap || !textarea) return;

    if (hasScroll) {
      const contentHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, scrollHeight));
      const currentHeight = wrap.offsetHeight || contentHeight;
      const height = Math.min(
        MAX_HEIGHT,
        Math.max(MIN_HEIGHT, Math.min(currentHeight, contentHeight)),
      );
      wrap.style.height = `${height}px`;
      wrap.style.maxHeight = `${contentHeight}px`;
    } else {
      wrap.style.height = '';
      wrap.style.maxHeight = '';
    }

    const selection = selectionRef.current;
    if (selection) {
      selectionRef.current = null;
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus({ preventScroll: true });
        el.setSelectionRange(selection.start, selection.end);
      });
    }
  }, [hasScroll, scrollHeight]);

  return { textareaRef, resizeRef, hasScroll };
};
